import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ProductCard from '@/components/base/ProductCard';
import { products } from '@/mocks/products';
import { faqList } from '@/mocks/faq';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const featuredProducts = products.filter((p) => ['biz-001', 'fest-001', 'fam-001'].includes(p.id));

  const advantages = [
    {
      icon: 'ri-shield-star-line',
      title: '品质保证',
      description: '精选全球优质鲍鱼产地，每只鲍鱼均经过严格品控筛选，确保品质上乘。',
    },
    {
      icon: 'ri-gift-line',
      title: '精工包装',
      description: '黑檀木、真皮、丝绸等多种高端材质可选，每一款礼盒都彰显尊贵格调。',
    },
    {
      icon: 'ri-truck-line',
      title: '冷链配送',
      description: '顺丰冷链全程温控，全国主要城市次日可达，确保鲍鱼新鲜送达。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '专属服务',
      description: '一对一客服顾问，支持礼盒定制、企业团购、贺卡代写等个性化服务。',
    },
  ];

  const stats = [
    { value: '10+', label: '年行业经验' },
    { value: '50,000+', label: '累计服务客户' },
    { value: '98%', label: '客户好评率' },
    { value: '30+', label: '全球合作产地' },
  ];

  const heroImageUrl = 'https://readdy.ai/api/search-image?query=Elegant%20dark%20luxury%20abalone%20gift%20box%20display%20on%20rich%20wooden%20table%20with%20gold%20accents%20and%20soft%20candlelight%2C%20premium%20seafood%20gourmet%20scene%2C%20warm%20amber%20and%20gold%20tones%2C%20artistic%20product%20photography%20with%20dark%20moody%20background%2C%20sophisticated%20and%20opulent%20atmosphere%2C%20shallow%20depth%20of%20field&width=1800&height=1000&seq=hero-abalone-luxury&orientation=landscape';

  const ctaImageUrl = 'https://readdy.ai/api/search-image?query=Luxury%20abalone%20gift%20box%20collection%20displayed%20elegantly%20on%20dark%20marble%20surface%20with%20gold%20decorative%20elements%2C%20warm%20ambient%20lighting%2C%20premium%20seafood%20gift%20set%20product%20photography%2C%20sophisticated%20dark%20and%20gold%20aesthetic%2C%20high%20end%20culinary%20scene&width=1600&height=600&seq=cta-abalone-gift&orientation=landscape';

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '鲍鱼盒子',
    url: 'https://www.baoyuhezi.com',
    description: '鲍鱼盒子专注高端鲍鱼礼盒定制，精选优质鲍鱼，搭配精美礼盒包装，适合商务送礼、节日馈赠与家庭享用。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.baoyuhezi.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImageUrl}
              alt="鲍鱼盒子 - 高端鲍鱼礼盒首选品牌"
              title="鲍鱼盒子高端鲍鱼礼盒"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 md:mb-6 tracking-wide">
              鲍鱼盒子
            </h1>
            <p className="text-sm md:text-lg text-background-50/90 max-w-2xl mb-8 md:mb-10 leading-relaxed">
              高端鲍鱼礼盒首选品牌 · 精选全球优质鲍鱼 · 尊贵非凡的心意之选
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/categories"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                探索礼盒系列
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-background-50/15 text-background-50 border border-background-50/30 rounded-full text-sm md:text-base font-semibold hover:bg-background-50/25 backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解品牌故事
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="relative -mt-16 md:-mt-20 z-10 w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto bg-background-50 rounded-2xl border border-background-200 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-background-200">
              {stats.map((stat, idx) => (
                <div key={idx} className="py-6 md:py-8 text-center px-3">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="w-full px-4 md:px-6 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                精选礼盒
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                三大场景精心搭配，满足您不同场合的送礼需求
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-product-shop>
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-500 hover:text-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                查看全部礼盒
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Brand Advantages */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                为什么选择鲍鱼盒子
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                四大核心优势，打造值得信赖的高端鲍鱼礼盒品牌
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {advantages.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-6 md:p-8 text-center border border-background-200 transition-all duration-300 hover:border-primary-200">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-5">
                    <i className={`${item.icon} text-2xl md:text-3xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <Link
                to="/categories?tab=business"
                className="group relative h-64 md:h-80 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 z-10"></div>
                <div className="absolute inset-0 bg-background-900">
                  <img
                    src="https://readdy.ai/api/search-image?query=Premium%20dark%20wood%20abalone%20gift%20box%20for%20business%20corporate%20gifting%2C%20elegant%20luxury%20packaging%20with%20gold%20accents%20on%20executive%20desk%2C%20sophisticated%20product%20photography%2C%20warm%20dark%20tones%2C%20professional%20setting&width=800&height=600&seq=cat-business-gift&orientation=landscape"
                    alt="商务送礼"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center p-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 mb-4">
                    <i className="ri-briefcase-4-line text-xl md:text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">商务送礼</h3>
                  <p className="text-sm text-background-50/80">高端商务馈赠之选</p>
                </div>
              </Link>

              <Link
                to="/categories?tab=festival"
                className="group relative h-64 md:h-80 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 z-10"></div>
                <div className="absolute inset-0 bg-background-900">
                  <img
                    src="https://readdy.ai/api/search-image?query=Chinese%20festival%20red%20and%20gold%20abalone%20gift%20box%20with%20traditional%20decorative%20elements%2C%20spring%20festival%20luxury%20gift%20set%2C%20celebratory%20atmosphere%2C%20elegant%20product%20photography%2C%20warm%20festive%20lighting%2C%20rich%20red%20and%20gold%20tones&width=800&height=600&seq=cat-festival-gift&orientation=landscape"
                    alt="节日礼盒"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center p-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 mb-4">
                    <i className="ri-gift-2-line text-xl md:text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">节日礼盒</h3>
                  <p className="text-sm text-background-50/80">传统佳节送礼首选</p>
                </div>
              </Link>

              <Link
                to="/categories?tab=family"
                className="group relative h-64 md:h-80 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 z-10"></div>
                <div className="absolute inset-0 bg-background-900">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20family%20dinner%20table%20with%20abalone%20seafood%20dish%20as%20centerpiece%2C%20cozy%20home%20dining%20scene%2C%20elegant%20table%20setting%20with%20warm%20lighting%2C%20family%20gathering%20atmosphere%2C%20gourmet%20home%20cooking%20photography&width=800&height=600&seq=cat-family-meal&orientation=landscape"
                    alt="家庭享用"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center p-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 mb-4">
                    <i className="ri-home-heart-line text-xl md:text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">家庭享用</h3>
                  <p className="text-sm text-background-50/80">品质生活之选</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                关于鲍鱼礼盒的常见疑问，这里为您一一解答
              </p>
            </div>
            <div className="space-y-3">
              {faqList.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
                  >
                    <span className="font-medium text-foreground-900 pr-4 text-sm md:text-base">
                      {faq.question}
                    </span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-45' : ''}`}>
                      <i className="ri-add-line text-foreground-500"></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === idx ? 'max-h-96 pb-5 md:pb-6 px-5 md:px-6' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full px-4 md:px-6 py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={ctaImageUrl}
              alt="立即选购鲍鱼礼盒"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          </div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4 md:mb-6">
              让每一份心意都尊贵非凡
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 md:mb-10 leading-relaxed">
              现在选购鲍鱼盒子礼盒套装，为您的商务伙伴、亲朋好友送上一份来自深海的珍贵礼物
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/categories"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即选购
              </Link>
              <a
                href="tel:400-888-9999"
                className="px-8 py-3.5 bg-transparent text-background-50 border border-background-50/40 rounded-full text-sm md:text-base font-semibold hover:bg-background-50/15 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                咨询客服
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}